/**
 * Product Card Buy Now Functionality
 * Handles quick add to cart from product cards across the theme
 */

(function() {
  'use strict';

  // Constants
  const BUY_NOW_BUTTON_SELECTOR = '.product-card__buy-now, .ss-product-card__buy-now, .ss-collection-product-card__buy-now';
  const SWATCH_SELECTOR = '.product-card__swatch[data-color], .ss-collection-product-card__swatch[data-color]';
  const swatchColourTable = [
    { handle: 'black', hex: '#000000' },
    { handle: 'white', hex: '#FFFFFF', light: true },
    { handle: 'grey', hex: '#808080' },
    { handle: 'red', hex: '#F61F1F' },
    { handle: 'blue', hex: '#005BD3' },
    { handle: 'navy', hex: '#282099' },
    { handle: 'green', hex: '#008000' },
    { handle: 'yellow', hex: '#FFFF00' },
    { handle: 'orange', hex: '#FFA500' },
    { handle: 'pink', hex: '#FFC0CB' },
    { handle: 'purple', hex: '#A54DCF' },
    { handle: 'purple-2', hex: '#444B98' },
    { handle: 'brown', hex: '#A52A2A' },
    { handle: 'beige', hex: '#EAD8AB', light: true },
    { handle: 'cream', hex: '#FFFDD0', light: true },
    { handle: 'tan', hex: '#D2B48C' },
    { handle: 'olive', hex: '#808000' },
    { handle: 'maroon', hex: '#800000' },
    { handle: 'teal', hex: '#008080' },
    { handle: 'coral', hex: '#FF7F50' },
    { handle: 'peach', hex: '#FFE5B4', light: true },
    { handle: 'lavender', hex: '#E6E6FA', light: true },
    { handle: 'mint', hex: '#98FF98', light: true },
    { handle: 'sage', hex: '#9DC183' },
    { handle: 'dark-green', hex: '#375546' },
    { handle: 'khaki', hex: '#88806A' },
    { handle: 'light-grey', hex: '#7B8CB2', light: true },
    { handle: 'chestnut-brown', hex: '#9A5630' },
    { handle: 'mint-green', hex: '#B4FFC1', light: true },
    { handle: 'soft-sage', hex: '#94BA90' },
    { handle: 'deep-grey', hex: '#5F5F5F' },
    { handle: 'burgundy', hex: '#641838' },
    { handle: 'forest-green', hex: '#05AA3D' },
    { handle: 'battleship-grey', hex: '#808080' },
    { handle: 'plum', hex: '#79608A' },
    { handle: 'hot-pink', hex: '#FF008F' },
    { handle: 'ivory-cream', hex: '#E3DCCB', light: true },
    { handle: 'coffee-brown', hex: '#A58771' },
    { handle: 'chocolate-brown', hex: '#6D4949' },
    { handle: 'peacock-green', hex: '#436E80' },
    { handle: 'apricot-brown', hex: '#DDB049' },
    { handle: 'alaska-blue', hex: '#67AFEE', light: true },
    { handle: 'midnight-blue', hex: '#1A2545' },
    { handle: 'matcha-calm', hex: '#B7F0BD', light: true },
    { handle: 'dusky-blue', hex: '#88ADDD', light: true },
    { handle: 'duck-egg-blue', hex: '#318CAE' },
    { handle: 'powder-pink', hex: '#DBBDD2', light: true },
    { handle: 'lime-zest-green', hex: '#C8E19E', light: true },
    { handle: 'truffle-brown', hex: '#764914' },
    { handle: 'carbon-black', hex: '#000000' }
  ];
  const swatchColourMap = {};
  const lightColourNames = [];

  // Build lookup arrays once per module load; the table is intentionally small.
  swatchColourTable.forEach(entry => {
    swatchColourMap[entry.handle] = entry.hex;
    if (entry.light) {
      lightColourNames.push(entry.handle);
    }
  });
  // Support both grey/gray spellings from existing colour handles.
  swatchColourMap.gray = swatchColourMap.grey;
  swatchColourMap['battleship-gray'] = swatchColourMap['battleship-grey'];
  const defaultSwatchColour = '#cccccc';
  const supportsColourCache = new Map();
  const MAX_COLOUR_CACHE_SIZE = 200;
  const supportsColour = value => {
    if (!value) return false;
    if (supportsColourCache.has(value)) {
      return supportsColourCache.get(value);
    }
    const result = typeof CSS !== 'undefined' && CSS.supports && CSS.supports('color', value);
    if (supportsColourCache.size >= MAX_COLOUR_CACHE_SIZE) {
      supportsColourCache.clear();
    }
    supportsColourCache.set(value, result);
    return result;
  };

  // Initialize buy now buttons when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductCardUI);
  } else {
    initProductCardUI();
  }

  function initProductCardUI() {
    initBuyNowButtons();
    applySwatchColours(document);

    document.addEventListener('shopify:section:load', event => {
      applySwatchColours(event.target);
    });
  }

  function initBuyNowButtons() {
    // Attach event listeners using event delegation
    document.body.addEventListener('click', function(e) {
      const buyNowBtn = e.target.closest(BUY_NOW_BUTTON_SELECTOR);
      
      // Only intercept clicks if this is actually a buy now button with variant data
      // This allows navigation links with the same class to work normally
      if (buyNowBtn && !buyNowBtn.disabled && buyNowBtn.hasAttribute('data-variant-id')) {
        e.preventDefault();
        e.stopPropagation();
        handleBuyNow(buyNowBtn);
      }
    });
  }

  function applySwatchColours(root) {
    if (!root || !root.querySelectorAll) return;

    root.querySelectorAll(SWATCH_SELECTOR).forEach(swatch => {
      const colourName = (swatch.getAttribute('data-color') || '').toLowerCase();
      const originalColourName = (swatch.getAttribute('data-color-name') || '').trim();
      const originalColourNameLower = originalColourName.toLowerCase();
      const resolvedColour = resolveSwatchColour(colourName, originalColourNameLower) || defaultSwatchColour;

      swatch.style.backgroundColor = resolvedColour;

      if (isLightColour(colourName, originalColourNameLower)) {
        swatch.style.border = '1px solid var(--color-border, #e5e7eb)';
      }
    });
  }

  // Handle compound names like "off-white" or "cream white".
  function isLightColour(colourName, originalColourNameLower) {
    if (lightColourNames.includes(colourName)) {
      return true;
    }
    const colourNameTokens = originalColourNameLower.split(/[\s-]+/);
    return colourNameTokens.some(part => lightColourNames.includes(part));
  }

  // Resolve via map, then CSS-supported names (with/without spaces/hyphens).
  function resolveSwatchColour(handle, originalColourNameLower) {
    if (handle && swatchColourMap[handle]) {
      return swatchColourMap[handle];
    }

    if (!originalColourNameLower) return null;

    if (supportsColour(originalColourNameLower)) {
      return originalColourNameLower;
    }

    const compact = originalColourNameLower.replace(/[\s-]/g, '');
    if (compact && supportsColour(compact)) {
      return compact;
    }

    return null;
  }

  async function handleBuyNow(button) {
    const productId = button.getAttribute('data-product-id');
    const variantId = button.getAttribute('data-variant-id');
    const cardLink = button.closest('.product-card, .ss-product-card, .ss-collection-product-card')?.querySelector('a[href]');
    const productUrl = button.getAttribute('data-product-url') || cardLink?.getAttribute('href');
    
    if (!variantId || !productUrl) {
      // Guard against misconfigured buttons with missing variant data.
      return;
    }

    const soldOutLabel = button.getAttribute('data-sold-out-label') || 'Sold out';

    // Store original button text
    const originalText = button.textContent;
    
    // Update button state
    button.disabled = true;
    button.textContent = 'Adding...';
    
    try {
      const productResponse = await fetch(productUrl.split('?')[0] + '.js', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!productResponse.ok) throw new Error('Unable to check availability');
      const currentProduct = await productResponse.json();
      const currentVariant = currentProduct.variants.find(variant => String(variant.id) === String(variantId));
      if (!currentVariant || currentVariant.available !== true) {
        button.textContent = soldOutLabel;
        button.disabled = true;
        button.classList.remove('error', 'success');
        return;
      }

      // Add to cart via Shopify Cart API
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw errorData;
      }

      const data = await response.json();
      
      // Show success state
      button.textContent = 'Added!';
      button.classList.add('success');
      
      // Update cart count if cart drawer/icon exists
      updateCartCount();
      
      // Show notification
      showNotification('Product added to cart', 'success');
      
      // Reset button after delay
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
        button.classList.remove('success');
      }, 2000);
      
    } catch (error) {
      // Show error state
      button.textContent = 'Error';
      button.classList.add('error');
      
      // Show error notification with descriptive message
      const errorMessage = error.description || error.message || 'Failed to add to cart';
      showNotification(errorMessage, 'error');
      
      // Reset button after delay
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
        button.classList.remove('error');
      }, 2000);
    }
  }

  async function updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Update cart count in header (adjust selector as needed)
      const cartCountElements = document.querySelectorAll('.cart-count, [data-cart-count]');
      cartCountElements.forEach(el => {
        el.textContent = cart.item_count;
        if (cart.item_count > 0) {
          el.style.display = '';
        }
      });
      
      // Trigger cart update event for other components
      document.dispatchEvent(new CustomEvent('cart:updated', { 
        detail: { cart: cart } 
      }));
      
    } catch (error) {
      // Cart count updates are optional; ignore errors quietly.
    }
  }

  function showNotification(message, type = 'info') {
    // Check if notification container exists
    let container = document.querySelector('.product-card-notification-container');
    
    if (!container) {
      container = document.createElement('div');
      container.className = 'product-card-notification-container';
      document.body.appendChild(container);
    }
    
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `product-card-notification product-card-notification--${type}`;
    notification.setAttribute('role', 'alert');
    notification.setAttribute('aria-live', 'polite');
    notification.textContent = message;
    
    container.appendChild(notification);
    
    // Trigger animation
    setTimeout(() => notification.classList.add('product-card-notification--visible'), 10);
    
    // Remove after delay
    setTimeout(() => {
      notification.classList.remove('product-card-notification--visible');
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }
})();
